import React, { useState } from 'react';
import {
  Search,
  Volume2,
  BookOpen,
  Code,
  Cog,
  Copy,
  Check,
  Lightbulb,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';

const KNOWLEDGE_DATA = {
  programmer: [
    {
      id: 'p1',
      german: 'die Schnittstelle',
      plural: 'die Schnittstellen',
      albanian: 'Ndërfaqja (API / Interface)',
      category: 'Backend & APIs',
      gender: 'fem',
      exampleDe: 'Die REST-Schnittstelle ermöglicht den sicheren Datenaustausch zwischen Frontend und Datenbank.',
      exampleSq: 'Ndërfaqja REST mundëson shkëmbimin e sigurt të të dhënave midis frontend-it dhe bazës së të dhënave.',
      tip: 'Në Gjermani, në vend të termit të plotë "Application Programming Interface", shpesh thuhet thjesht "die API" ose "die Schnittstelle".',
    },
    {
      id: 'p2',
      german: 'die Bereitstellung',
      plural: 'die Bereitstellungen',
      albanian: 'Vendosja në prodhim / Deployment',
      category: 'DevOps & Cloud',
      gender: 'fem',
      exampleDe: 'Die automatisierte Bereitstellung in der Cloud erfolgt nach jedem erfolgreichen Build über GitHub Actions.',
      exampleSq: 'Vendosja e automatizuar në cloud kryhet pas çdo ndërtimi të suksesshëm përmes GitHub Actions.',
      tip: 'Shumë zhvillues gjermanë përdorin edhe fjalën "das Deployment", por në dokumentacione zyrtare përdoret "die Bereitstellung".',
    },
    {
      id: 'p3',
      german: 'die Fehlerbehebung',
      plural: 'die Fehlerbehebungen',
      albanian: 'Riparimi i gabimeve / Debugging / Bugfix',
      category: 'Zhvillim & Testim',
      gender: 'fem',
      exampleDe: 'Für die Fehlerbehebung im Produktionssystem wurde ein Notfall-Patch eingespielt.',
      exampleSq: 'Për riparimin e gabimit në sistemin e prodhimit u aplikua një patch emergjent.',
      tip: 'Në takime do të dëgjoni shpesh foljen "einen Fehler beheben" (të riparosh një gabim).',
    },
    {
      id: 'p4',
      german: 'die Benutzeroberfläche',
      plural: 'die Benutzeroberflächen',
      albanian: 'Ndërfaqja e përdoruesit (UI / User Interface)',
      category: 'Frontend & UI',
      gender: 'fem',
      exampleDe: 'Die neue Benutzeroberfläche wurde mit React und Tailwind CSS barrierefrei gestaltet.',
      exampleSq: 'Ndërfaqja e re e përdoruesit u dizajnua me React dhe Tailwind CSS pa pengesa aksesueshmërie.',
      tip: '"Barrierefrei" do të thotë e përshtatshme për personat me nevoja të veçanta, një standard ligjor i detyrueshëm në Gjermani.',
    },
    {
      id: 'p5',
      german: 'die Zustandsverwaltung',
      plural: 'die Zustandsverwaltungen',
      albanian: 'Menaxhimi i gjendjes (State Management)',
      category: 'Frontend & UI',
      gender: 'fem',
      exampleDe: 'Wir nutzen Redux Toolkit für eine globale und vorhersehbare Zustandsverwaltung.',
      exampleSq: 'Ne përdorim Redux Toolkit për një menaxhim global dhe të parashikueshëm të gjendjes.',
      tip: 'Fjala rrjedh nga "der Zustand" (gjendja / state) dhe "die Verwaltung" (administrimi/menaxhimi).',
    },
    {
      id: 'p6',
      german: 'die Datenbankabfrage',
      plural: 'die Datenbankabfragen',
      albanian: 'Kërkesa / Query në bazën e të dhënave',
      category: 'Backend & APIs',
      gender: 'fem',
      exampleDe: 'Komplexe Datenbankabfragen sollten indiziert werden, um die Latenzzeit zu minimieren.',
      exampleSq: 'Kërkesat komplekse në bazën e të dhënave duhet të indeksohen për të minimizuar vonesën.',
      tip: '"Die Abfrage" = kërkesë/pyetje në bazë të dhënash; folja është "abfragen" (bëj query).',
    },
    {
      id: 'p7',
      german: 'die Versionsverwaltung',
      plural: 'die Versionsverwaltungen',
      albanian: 'Kontrolli i versioneve (Version Control / Git)',
      category: 'Git & Workflow',
      gender: 'fem',
      exampleDe: 'Alle Entwickler arbeiten nach dem Git-Flow-Modell in der zentralen Versionsverwaltung.',
      exampleSq: 'Të gjithë zhvilluesit punojnë sipas modelit Git-Flow në kontrollin qendror të versioneve.',
      tip: '"Einen Branch mergen" shpesh përkthehet zyrtarisht si "einen Zweig zusammenführen".',
    },
    {
      id: 'p8',
      german: 'die Ausnahmebehandlung',
      plural: 'die Ausnahmebehandlungen',
      albanian: 'Trajtimi i përjashtimeve (Exception Handling)',
      category: 'Zhvillim & Testim',
      gender: 'fem',
      exampleDe: 'Eine saubere Ausnahmebehandlung verhindert unkontrollierte Abstürze der Anwendung.',
      exampleSq: 'Një trajtim i pastër i gabimeve parandalon mbylljen e papritur (crash) të aplikacionit.',
      tip: 'Blloku try/catch njihet si mekanizëm i "die Ausnahme" (përjashtimit).',
    },
    {
      id: 'p9',
      german: 'die Skalierbarkeit',
      plural: '-',
      albanian: 'Shkallëzueshmëria (Scalability)',
      category: 'Arkitekturë & Cloud',
      gender: 'fem',
      exampleDe: 'Die Microservice-Architektur verbessert die Skalierbarkeit unseres Online-Shops.',
      exampleSq: 'Arkitektura microservices përmirëson shkallëzueshmërinë e dyqanit tonë online.',
      tip: 'Folja përkatëse është "skalieren" (të shkallëzosh horizontalisht ose vertikalisht).',
    },
    {
      id: 'p10',
      german: 'das Lastenausgleichssystem',
      plural: 'die Lastenausgleichssysteme',
      albanian: 'Balancuesi i ngarkesës (Load Balancer)',
      category: 'DevOps & Cloud',
      gender: 'neu',
      exampleDe: 'Der Load Balancer verteilt eingehende HTTP-Anfragen gleichmäßig auf mehrere Server-Instanzen.',
      exampleSq: 'Balancuesi i ngarkesës shpërndan kërkesat hyrëse HTTP në mënyrë të barabartë në disa instanca serveri.',
      tip: 'Zakonisht inxhinierët gjermanë përdorin fjalën "der Load Balancer", por në specifikime do të shihni "der Lastverteiler".',
    },
    {
      id: 'p11',
      german: 'die Sprint-Planung',
      plural: 'die Sprint-Planungen',
      albanian: 'Planifikimi i Sprint-it (Agile / Scrum)',
      category: 'Agile & Scrum',
      gender: 'fem',
      exampleDe: 'In der Sprint-Planung schätzen wir die Story Points für die anstehenden User Stories.',
      exampleSq: 'Në planifikimin e sprint-it ne vlerësojmë Story Points për kërkesat e ardhshme të përdoruesve.',
      tip: '"Schätzen" është folja kyçe për vlerësimin e kohës ose pikëve të vështirësisë në Scrum.',
    },
    {
      id: 'p12',
      german: 'die Codeüberprüfung',
      plural: 'die Codeüberprüfungen',
      albanian: 'Rishikimi i kodit (Code Review)',
      category: 'Git & Workflow',
      gender: 'fem',
      exampleDe: 'Kein Code darf ohne mindestens zwei Freigaben in der Codeüberprüfung gemergt werden.',
      exampleSq: 'Asnjë kod nuk lejohet të bëhet merge pa të paktën dy aprovime në rishikimin e kodit.',
      tip: '"Freigeben" do të thotë të aprovosh / japësh leje (Approve pull request).',
    },
    {
      id: 'p13',
      german: 'die Anforderung',
      plural: 'die Anforderungen',
      albanian: 'Kërkesa teknike / Requirement',
      category: 'Analizë & Produkte',
      gender: 'fem',
      exampleDe: 'Wir müssen die technischen Anforderungen vor Beginn der Entwicklung gemeinsam klären.',
      exampleSq: 'Duhet t’i sqarojmë bashkë kërkesat teknike para fillimit të zhvillimit.',
      tip: 'Në projektet gjermane përdoret shpesh shprehja "Anforderungen aufnehmen" për mbledhjen e kërkesave.',
    },
    {
      id: 'p14',
      german: 'die Fehleranalyse',
      plural: 'die Fehleranalysen',
      albanian: 'Analiza e gabimit / Troubleshooting',
      category: 'Zhvillim & Testim',
      gender: 'fem',
      exampleDe: 'Die Fehleranalyse hat gezeigt, dass die Ursache in der Validierung lag.',
      exampleSq: 'Analiza e gabimit tregoi se shkaku ishte te validimi.',
      tip: 'Folja praktike është "einen Fehler analysieren" dhe përdoret në ticket-a e raporte.',
    },
    {
      id: 'p15',
      german: 'die Testabdeckung',
      plural: 'die Testabdeckungen',
      albanian: 'Mbulimi me teste (Test Coverage)',
      category: 'Zhvillim & Testim',
      gender: 'fem',
      exampleDe: 'Die Testabdeckung muss vor dem Release auf mindestens 80 Prozent steigen.',
      exampleSq: 'Mbulimi me teste duhet të rritet në të paktën 80 për qind para publikimit.',
      tip: '"Die Abdeckung erhöhen" do të thotë ta rrisësh mbulimin e testeve.',
    },
    {
      id: 'p16',
      german: 'die Schnittstellendokumentation',
      plural: 'die Schnittstellendokumentationen',
      albanian: 'Dokumentacioni i API-së',
      category: 'Backend & APIs',
      gender: 'fem',
      exampleDe: 'Die Schnittstellendokumentation beschreibt alle Endpunkte und ihre Antwortformate.',
      exampleSq: 'Dokumentacioni i API-së përshkruan të gjitha endpoint-et dhe formatet e përgjigjeve.',
      tip: 'Termi "Endpunkt" përdoret shumë në dokumentimin e REST API-ve.',
    },
    {
      id: 'p17',
      german: 'die Versionsfreigabe',
      plural: 'die Versionsfreigaben',
      albanian: 'Miratimi i versionit / Release Approval',
      category: 'Git & Workflow',
      gender: 'fem',
      exampleDe: 'Für die Versionsfreigabe benötigen wir noch die Zustimmung des Product Owners.',
      exampleSq: 'Për miratimin e versionit na duhet ende pëlqimi i Product Owner-it.',
      tip: 'Në komunikim do të dëgjoni edhe "das Release freigeben".',
    },
    {
      id: 'p18',
      german: 'die Produktionsumgebung',
      plural: 'die Produktionsumgebungen',
      albanian: 'Mjedisi i prodhimit (Production Environment)',
      category: 'DevOps & Cloud',
      gender: 'fem',
      exampleDe: 'Die Änderung darf erst nach erfolgreicher Prüfung in die Produktionsumgebung übertragen werden.',
      exampleSq: 'Ndryshimi mund të dërgohet në mjedisin e prodhimit vetëm pas kontrollit të suksesshëm.',
      tip: 'Krahasoje me "die Entwicklungsumgebung" dhe "die Testumgebung".',
    },
    {
      id: 'p19',
      german: 'die Zugriffsberechtigung',
      plural: 'die Zugriffsberechtigungen',
      albanian: 'Leja e qasjes / Access Permission',
      category: 'Siguri & Infrastrukturë',
      gender: 'fem',
      exampleDe: 'Für den Zugriff auf die Datenbank benötigen Sie eine zusätzliche Zugriffsberechtigung.',
      exampleSq: 'Për qasje në bazën e të dhënave ju duhet një leje shtesë.',
      tip: 'Folja "Zugriff gewähren" do të thotë të japësh qasje.',
    },
    {
      id: 'p20',
      german: 'die Rückwärtskompatibilität',
      plural: '-',
      albanian: 'Pajtueshmëria prapa (Backward Compatibility)',
      category: 'Arkitekturë & Cloud',
      gender: 'fem',
      exampleDe: 'Die neue API muss aus Gründen der Rückwärtskompatibilität ältere Clients weiterhin unterstützen.',
      exampleSq: 'API-ja e re duhet t’i mbështesë ende klientët e vjetër për pajtueshmëri prapa.',
      tip: 'Folja "unterstützen" përdoret për versionet ose klientët që vazhdojnë të punojnë.',
    },
  ],
  mechatronics: [
    {
      id: 'm1',
      german: 'die speicherprogrammierbare Steuerung (SPS)',
      plural: 'die speicherprogrammierbaren Steuerungen',
      albanian: 'Kontrolluesi Logjik i Programueshëm (PLC)',
      category: 'PLC & Automatikë',
      gender: 'fem',
      exampleDe: 'Die Siemens S7-1500 SPS steuert den gesamten Bestückungsautomaten in Taktzeit.',
      exampleSq: 'Siemens S7-1500 PLC kontrollon të gjithë makinën automatike të montimit brenda ciklit kohor.',
      tip: 'Në Gjermani, fjala PLC pothuajse gjithmonë quhet "die SPS". Standardi i programimit është IEC 61131-3.',
    },
    {
      id: 'm2',
      german: 'der Schaltplan',
      plural: 'die Schaltpläne',
      albanian: 'Skema elektrike / Diagrami i qarkut',
      category: 'Elektroteknikë',
      gender: 'masc',
      exampleDe: 'Laut Schaltplan liegt das Steuersignal an Klemme 14 des Not-Aus-Relais an.',
      exampleSq: 'Sipas skemës elektrike, sinjali i kontrollit ndodhet te klema 14 e relesë së emergjencës.',
      tip: 'Dokumenti bazë në çdo kabinë elektrike (Schaltschrank). Sinonim: "der Stromlaufplan".',
    },
    {
      id: 'm3',
      german: 'der Schaltschrank',
      plural: 'die Schaltschränke',
      albanian: 'Kabina / Paneli elektrik i kontrollit',
      category: 'Elektroteknikë',
      gender: 'masc',
      exampleDe: 'Im Schaltschrank sind die Sicherungen, Netzteile und SPS-Baugruppen untergebracht.',
      exampleSq: 'Në kabinën elektrike janë vendosur siguresat, ushqyesit dhe modulet e PLC-së.',
      tip: 'Kërkon gjithmonë respektimin e rregullave të mbrojtjes nga pluhuri dhe lagështia (kodi IP).',
    },
    {
      id: 'm4',
      german: 'der Näherungsschalter',
      plural: 'die Näherungsschalter',
      albanian: 'Sensori i afërsisë (Proximity Sensor)',
      category: 'Sensorë & Aktuatorë',
      gender: 'masc',
      exampleDe: 'Der induktive Näherungsschalter erkennt metallische Werkstücke berührungslos.',
      exampleSq: 'Sensori induktiv i afërsisë dallon pjesët metalike pa pasur nevojë për kontakt fizik.',
      tip: '"Induktiv" për metale, "kapazitiv" për lëngje/jo-metale, "optisch" me dritë infrakuqe.',
    },
    {
      id: 'm5',
      german: 'der Aktor',
      plural: 'die Aktoren',
      albanian: 'Aktuatori / Elementi ekzekutues (motor, cilindër)',
      category: 'Sensorë & Aktuatorë',
      gender: 'masc',
      exampleDe: 'Aktoren wie Zylinder und Servomotoren setzen elektrische Steuersignale in mechanische Bewegung um.',
      exampleSq: 'Aktuatorët si cilindrat dhe servomotorët konvertojnë sinjalet elektrike të kontrollit në lëvizje mekanike.',
      tip: 'Parimi kryesor i mekatronikës: "Sensor → SPS → Aktor" (Sensori lexon, PLC vendos, Aktuatori lëviz).',
    },
    {
      id: 'm6',
      german: 'das Pneumatikventil',
      plural: 'die Pneumatikventile',
      albanian: 'Valvula pneumatike (e ajrit të kompresuar)',
      category: 'Pneumatikë & Hidraulikë',
      gender: 'neu',
      exampleDe: 'Das 5/2-Wege-Magnetventil steuert das Aus- und Einfahren des doppeltwirkenden Zylinders.',
      exampleSq: 'Valvula elektromagnetike 5/2 kontrollon hapjen dhe mbylljen e cilindrit me dy veprime.',
      tip: 'Presioni standart industrial në linjat gjermane të pneumatikës është zakonisht 6 bar.',
    },
    {
      id: 'm7',
      german: 'der Industrieroboter',
      plural: 'die Industrieroboter',
      albanian: 'Roboti industrial (KUKA, ABB, Fanuc)',
      category: 'Robotikë & Industri 4.0',
      gender: 'masc',
      exampleDe: 'Der 6-Achs-Industrieroboter übernimmt das präzise Schutzgasschweißen der Karosserieteile.',
      exampleSq: 'Roboti industrial me 6 akse merr përsipër saldimin preciz me gaz mbrojtës të pjesëve të karrocerisë.',
      tip: 'Në Gjermani, pikat e lëvizjes mësohen shpesh me Teach-Pendant ("das Handbediengerät").',
    },
    {
      id: 'm8',
      german: 'die vorbeugende Instandhaltung',
      plural: 'die vorbeugenden Instandhaltungen',
      albanian: 'Mirëmbajtja parandaluese (Preventive Maintenance)',
      category: 'Mirëmbajtje & Siguri',
      gender: 'fem',
      exampleDe: 'Durch vorbeugende Instandhaltung werden Kugellager ausgetauscht, bevor ein Lagerschaden entsteht.',
      exampleSq: 'Përmes mirëmbajtjes parandaluese, kushinetat ndërrohen para se të ndodhë dëmtimi i tyre.',
      tip: 'Kjo është kërkesë kyçe në fabrikat gjermane për të shmangur ndalesat e kushtueshme (Stillstand).',
    },
    {
      id: 'm9',
      german: 'der Not-Aus-Taster',
      plural: 'die Not-Aus-Taster',
      albanian: 'Butoni i ndalimit emergjent (Emergency Stop / Mushroom Button)',
      category: 'Mirëmbajtje & Siguri',
      gender: 'masc',
      exampleDe: 'Beim Betätigen des Not-Aus-Tasters wird die Anlage sofort spannungsfrei geschaltet.',
      exampleSq: 'Me shtypjen e butonit Not-Aus, impiantit i hiqet menjëherë tensioni elektrik.',
      tip: 'Është gjithmonë me ngjyrë të kuqe mbi bazament të verdhë dhe duhet të zhbllokohet mekanikisht me rrotullim.',
    },
    {
      id: 'm10',
      german: 'die technische Zeichnung',
      plural: 'die technischen Zeichnungen',
      albanian: 'Vizatimi teknik (CAD / Blueprint)',
      category: 'CAD & Projektim',
      gender: 'fem',
      exampleDe: 'Die Maße und Formtoleranzen müssen exakt mit der technischen Zeichnung übereinstimmen.',
      exampleSq: 'Përmasat dhe tolerancat gjeometrike duhet të përputhen saktësisht me vizatimin teknik.',
      tip: 'Në Gjermani, standardi DIN EN ISO përcakton simbolet dhe vizatimet teknike.',
    },
    {
      id: 'm11',
      german: 'das Drehmoment',
      plural: 'die Drehmomente',
      albanian: 'Momenti rrotullues (Torque / Nm)',
      category: 'Mekanikë',
      gender: 'neu',
      exampleDe: 'Die Schrauben am Flansch müssen mit einem Drehmoment von 45 Nm angezogen werden.',
      exampleSq: 'Vidat në flanxhë duhet të shtrëngohen me një moment rrotullues prej 45 Nm.',
      tip: 'Vegla përkatëse quhet "der Drehmomentschlüssel" (çelësi dinamometrik).',
    },
    {
      id: 'm12',
      german: 'die Schichtübergabe',
      plural: 'die Schichtübergaben',
      albanian: 'Dorëzimi i turnit të punës (Shift Handover)',
      category: 'Punë në Fabrikë',
      gender: 'fem',
      exampleDe: 'Bei der Schichtübergabe informieren wir die Kollegen über behobene Störungen und offene Wartungsarbeiten.',
      exampleSq: 'Gjatë dorëzimit të turnit, ne informojmë kolegët mbi avaritë e zgjidhura dhe punët e hapura të mirëmbajtjes.',
      tip: 'Proces kritik në industrinë gjermane me 3 turne (Frühschicht, Spätschicht, Nachtschicht).',
    },
    {
      id: 'm13',
      german: 'der Näherungssensor',
      plural: 'die Näherungssensoren',
      albanian: 'Sensori i afërsisë (Proximity Sensor)',
      category: 'Sensorë & Aktuatorë',
      gender: 'masc',
      exampleDe: 'Der Näherungssensor erkennt das Werkstück und gibt das Signal an die SPS weiter.',
      exampleSq: 'Sensori i afërsisë e njeh pjesën e punës dhe ia dërgon sinjalin PLC-së.',
      tip: 'Në punë përdoret edhe termi "induktiver Näherungsschalter" për sensorin induktiv.',
    },
    {
      id: 'm14',
      german: 'der Frequenzumrichter',
      plural: 'die Frequenzumrichter',
      albanian: 'Konvertuesi i frekuencës / Inverter',
      category: 'Elektroteknikë',
      gender: 'masc',
      exampleDe: 'Mit dem Frequenzumrichter lässt sich die Drehzahl des Motors präzise regeln.',
      exampleSq: 'Me konvertuesin e frekuencës mund të rregullohet saktë shpejtësia e motorit.',
      tip: 'Folja teknike është "die Drehzahl regeln" — ta rregullosh shpejtësinë e rrotullimit.',
    },
    {
      id: 'm15',
      german: 'die Inbetriebnahme',
      plural: 'die Inbetriebnahmen',
      albanian: 'Vënia në punë / Commissioning',
      category: 'Mirëmbajtje & Siguri',
      gender: 'fem',
      exampleDe: 'Vor der Inbetriebnahme müssen alle Sicherheitsfunktionen geprüft werden.',
      exampleSq: 'Para vënies në punë duhet të kontrollohen të gjitha funksionet e sigurisë.',
      tip: 'Shprehja "eine Anlage in Betrieb nehmen" është shumë e zakonshme në fabrikë.',
    },
    {
      id: 'm16',
      german: 'die Störungsanalyse',
      plural: 'die Störungsanalysen',
      albanian: 'Analiza e avarisë / Fault Analysis',
      category: 'Mirëmbajtje & Siguri',
      gender: 'fem',
      exampleDe: 'Für die Störungsanalyse benötigen wir den aktuellen Diagnosepuffer der SPS.',
      exampleSq: 'Për analizën e avarisë na duhet buffer-i aktual diagnostikues i PLC-së.',
      tip: 'Mos e ngatërro "die Störung" (avari) me "der Stillstand" (ndalesë).',
    },
    {
      id: 'm17',
      german: 'der Aktor',
      plural: 'die Aktoren',
      albanian: 'Aktuatori (Actuator)',
      category: 'Sensorë & Aktuatorë',
      gender: 'masc',
      exampleDe: 'Der Aktor setzt das Steuersignal der SPS in eine mechanische Bewegung um.',
      exampleSq: 'Aktuatori e shndërron sinjalin e kontrollit të PLC-së në lëvizje mekanike.',
      tip: 'Çifti bazë në automatizim është "Sensor und Aktor".',
    },
    {
      id: 'm18',
      german: 'die Druckluftversorgung',
      plural: 'die Druckluftversorgungen',
      albanian: 'Furnizimi me ajër të kompresuar',
      category: 'Pneumatikë & Hidraulikë',
      gender: 'fem',
      exampleDe: 'Eine unzureichende Druckluftversorgung kann zu unregelmäßigen Bewegungen des Zylinders führen.',
      exampleSq: 'Furnizimi i pamjaftueshëm me ajër të kompresuar mund të shkaktojë lëvizje jo të rregullta të cilindrit.',
      tip: 'Termat kryesorë janë "der Druck" (presioni) dhe "die Versorgung" (furnizimi).',
    },
    {
      id: 'm19',
      german: 'die vorbeugende Instandhaltung',
      plural: 'die vorbeugenden Instandhaltungen',
      albanian: 'Mirëmbajtja parandaluese (Preventive Maintenance)',
      category: 'Mirëmbajtje & Siguri',
      gender: 'fem',
      exampleDe: 'Durch vorbeugende Instandhaltung vermeiden wir ungeplante Produktionsausfälle.',
      exampleSq: 'Me mirëmbajtje parandaluese shmangim ndërprerjet e paplanifikuara të prodhimit.',
      tip: 'Në industri përdoret edhe shkurtesa "PM" për "Preventive Maintenance".',
    },
    {
      id: 'm20',
      german: 'die Maßtoleranz',
      plural: 'die Maßtoleranzen',
      albanian: 'Toleranca e përmasës / Dimensional Tolerance',
      category: 'CAD & Projektim',
      gender: 'fem',
      exampleDe: 'Die Maßtoleranz der Bohrung darf laut Zeichnung nur 0,05 Millimeter betragen.',
      exampleSq: 'Toleranca e vrimës sipas vizatimit mund të jetë vetëm 0,05 milimetra.',
      tip: 'Fjala "laut Zeichnung" do të thotë “sipas vizatimit” dhe përdoret shpesh në prodhim.',
    },
  ],
};

const GENDER_BADGES = {
  masc: { label: 'der (m)', bg: 'bg-blue-50 text-blue-700 border-blue-200' },
  fem: { label: 'die (f)', bg: 'bg-rose-50 text-rose-700 border-rose-200' },
  neu: { label: 'das (n)', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export default function TechnicalKnowledgeBase({ profession }) {
  const isProgrammer = profession === 'programmer';
  const data = KNOWLEDGE_DATA[isProgrammer ? 'programmer' : 'mechatronics'] || [];

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [copiedId, setCopiedId] = useState(null);

  const categories = ['all', ...Array.from(new Set(data.map((item) => item.category)))];

  const filteredData = data.filter((item) => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch =
      item.german.toLowerCase().includes(search.toLowerCase()) ||
      item.albanian.toLowerCase().includes(search.toLowerCase()) ||
      item.exampleDe.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const speak = (text) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'de-DE';
    utterance.rate = 0.9;
    window.speechSynthesis.speak(utterance);
  };

  const copy = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl bg-white p-5 shadow-md">
        <div>
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-zinc-950 text-[#FFCE00]">
              {isProgrammer ? <Code size={18} /> : <Cog size={18} />}
            </div>
            <h3 className="text-base font-extrabold text-zinc-950">
              {isProgrammer
                ? 'Fjalori & Terminologjia Teknike e Programimit'
                : 'Fjalori & Terminologjia Teknike e Mekatronikës'}
            </h3>
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            Terma të saktë me artikuj (der/die/das), audio shqiptim, dhe shembuj nga tregu gjerman i punës.
          </p>
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kërko gjermanisht ose shqip..."
            className="h-9 rounded-xl border-zinc-200 bg-zinc-50 pl-9 text-xs focus-visible:bg-white focus-visible:ring-[#FFCE00]"
          />
        </div>
      </div>

      {/* Categories Filter */}
      <div className="flex flex-wrap gap-1.5">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setSelectedCategory(cat)}
            className={`rounded-xl px-3 py-1.5 text-xs font-bold transition-all ${
              selectedCategory === cat
                ? 'bg-zinc-950 text-[#FFCE00] shadow-sm'
                : 'bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-400 hover:text-zinc-900'
            }`}
          >
            {cat === 'all' ? 'Të gjitha fushat' : cat}
          </button>
        ))}
      </div>

      {/* Grid of Knowledge Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredData.map((item) => {
          const badge = GENDER_BADGES[item.gender] || GENDER_BADGES.fem;
          return (
            <Card
              key={item.id}
              className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border-zinc-200/90 bg-white p-5 shadow-xs hover:border-zinc-400 hover:shadow-md transition-all"
            >
              <div>
                {/* Top Row: Word & Actions */}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`rounded-md px-2 py-0.5 text-[10px] font-extrabold uppercase border ${badge.bg}`}
                      >
                        {badge.label}
                      </span>
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                        {item.category}
                      </span>
                    </div>
                    <h4 className="mt-2 text-base font-extrabold text-zinc-950 tracking-tight flex items-center gap-2">
                      {item.german}
                    </h4>
                    {item.plural && item.plural !== '-' && (
                      <p className="text-[11px] text-zinc-400 italic">Shumësi: {item.plural}</p>
                    )}
                  </div>

                  {/* Audio & Copy */}
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => speak(item.german)}
                      className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition"
                      title="Dëgjo fjalën"
                    >
                      <Volume2 size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => copy(item.id, `${item.german} - ${item.albanian}`)}
                      className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition"
                      title="Kopjo termin"
                    >
                      {copiedId === item.id ? (
                        <Check size={16} className="text-emerald-600" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                  </div>
                </div>

                {/* Albanian Meaning */}
                <div className="mt-3 rounded-xl bg-zinc-50 p-2.5 border border-zinc-100">
                  <span className="text-[10px] font-extrabold text-zinc-400 uppercase tracking-wider block">
                    Përkthimi & Kuptimi
                  </span>
                  <p className="text-xs font-bold text-zinc-800 mt-0.5">{item.albanian}</p>
                </div>

                {/* German Example Sentence */}
                <div className="mt-3 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-extrabold text-zinc-400 uppercase tracking-wider">
                      Shembull në Punë (Gjermanisht)
                    </span>
                    <button
                      type="button"
                      onClick={() => speak(item.exampleDe)}
                      className="text-[11px] font-bold text-zinc-500 hover:text-zinc-900 flex items-center gap-1"
                    >
                      <Volume2 size={12} /> Dëgjo
                    </button>
                  </div>
                  <p className="text-xs font-semibold text-zinc-900 leading-relaxed bg-[#FFCE00]/10 rounded-lg p-2.5 border border-[#FFCE00]/20">
                    "{item.exampleDe}"
                  </p>
                  <p className="text-[11px] text-zinc-500 italic pl-1">"{item.exampleSq}"</p>
                </div>
              </div>

              {/* Workplace context tip */}
              {item.tip && (
                <div className="mt-4 pt-3 border-t border-zinc-100 flex items-start gap-2 text-[11px] text-zinc-500">
                  <Lightbulb size={13} className="text-amber-500 shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-zinc-700">Kontekst Gjerman:</strong> {item.tip}
                  </span>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {filteredData.length === 0 && (
        <div className="rounded-2xl bg-white p-10 text-center text-zinc-400 shadow-md">
          <BookOpen className="mx-auto h-8 w-8 text-zinc-300 mb-2" />
          <p className="text-xs font-bold">Nuk u gjet asnjë term teknik për këtë kërkim.</p>
        </div>
      )}
    </div>
  );
}
